import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, CreditCard, Wallet, Send, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FLAVORS, PRICING } from "../data/products";
import { createCustomOrder, createDeposit, startStripeCheckout } from "../lib/api";
import { PayPalDeposit } from "./PayPalDeposit";

const DEPOSIT = 25;
const minDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 5);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const CustomOrder = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", servings: 12,
    base_flavor: "", filling: "", dietary_notes: "", fulfillment: "pickup",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payResult, setPayResult] = useState(null);
  const [paying, setPaying] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Please choose an event date (5 days notice required).");
    if (!form.base_flavor) return toast.error("Please choose a baked good.");
    if (!form.filling) return toast.error("Please choose a price / quantity option.");
    setSubmitting(true);
    try {
      const res = await createCustomOrder({
        ...form,
        servings: Number(form.servings),
        event_date: format(date, "yyyy-MM-dd"),
      });
      setOrderId(res.order_id);
      setPayResult(null);
      setPayOpen(true);
      toast.success("Request received! Reserve your date with a deposit.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const pay = async (method) => {
    setPaying(true);
    try {
      const res = await createDeposit({
        order_id: orderId, method, amount: DEPOSIT, name: form.name, email: form.email,
      });
      setPayResult(res);
      toast.success(`Deposit reference ${res.reference} created.`);
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  const payWithStripe = async () => {
    setPaying(true);
    try {
      // Redirects to Stripe Checkout; return happens on /payment/success.
      await startStripeCheckout({ order_id: orderId, name: form.name, email: form.email });
    } catch (err) {
      toast.error("Could not start card checkout. Please try again.");
      setPaying(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <section id="custom-order" className="py-20 lg:py-28 bg-creamSoft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-olive">Baked Goods</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-ink tracking-tight">
            Order your dream baked delight
          </h2>
          <p className="mt-5 text-[#524B46] leading-relaxed">
            Home Baked delight — all completely sugar-free. Tell us your vision and reserve your date with
            a refundable <strong className="text-ink">deposit</strong> for large orders. Balance due at pickup or delivery.
          </p>
          <p className="mt-6 text-sm text-[#787069]">
            ⏱ 5 days notice minimum · 🏠 Local pickup in Tampa · 🚚 Next-day shipping available
          </p>
        </div>

        <div className="lg:col-span-7 w-full">
          <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-[0_30px_70px_-50px_rgba(45,40,37,0.5)]">
            <img
              src="/products/custom-order-picnic-v3.jpg"
              alt="Two happy children enjoying almond-flour raisin cookies at a sunny garden picnic"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="deposit-dialog" className="sm:max-w-md bg-cream">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">Reserve your date</DialogTitle>
            <DialogDescription>
              Secure your booking with a ${DEPOSIT} deposit. Choose your preferred method.
            </DialogDescription>
          </DialogHeader>

          {payResult ? (
            <div data-testid="deposit-result" className="text-center py-2">
              <CheckCircle2 size={48} className="mx-auto text-olive" />
              <p className="mt-3 font-serif text-xl text-ink">You're all set!</p>
              <p className="mt-1 text-sm text-[#524B46]">
                Reference <b>{payResult.reference}</b> · ${payResult.amount.toFixed(2)} via {payResult.method}
              </p>
              <div className="mt-4 text-left text-sm bg-white rounded-xl border border-[#EFE9E0] p-4 space-y-2">
                <p className="text-[#524B46]">{payResult.instructions.note}</p>
                {payResult.instructions.paypal_url && (
                  <a href={payResult.instructions.paypal_url} target="_blank" rel="noreferrer"
                    className="block text-terracotta font-medium underline break-all">{payResult.instructions.paypal_url}</a>
                )}
                {payResult.instructions.zelle_email && (
                  <button onClick={() => copy(payResult.instructions.zelle_email)}
                    className="flex items-center gap-2 text-terracotta font-medium">
                    <Copy size={14} /> {payResult.instructions.zelle_email}
                  </button>
                )}
                {payResult.instructions.checkout_url && (
                  <a href={payResult.instructions.checkout_url} target="_blank" rel="noreferrer"
                    data-testid="deposit-checkout-link"
                    className="block text-terracotta font-medium underline break-all">
                    Open secure checkout →
                  </a>
                )}
              </div>
              <Button onClick={() => setPayOpen(false)} data-testid="deposit-done"
                className="mt-5 w-full rounded-full bg-olive hover:bg-olive-hover text-cream h-12">Done</Button>
            </div>
          ) : (
            <Tabs defaultValue="stripe" className="mt-2">
              <TabsList className="grid grid-cols-3 w-full bg-surface">
                <TabsTrigger value="stripe" data-testid="pay-tab-stripe">Card</TabsTrigger>
                <TabsTrigger value="paypal" data-testid="pay-tab-paypal">PayPal</TabsTrigger>
                <TabsTrigger value="zelle" data-testid="pay-tab-zelle">Zelle</TabsTrigger>
              </TabsList>
              <TabsContent value="stripe" className="pt-4">
                <p className="text-sm text-[#524B46] flex items-start gap-2">
                  <CreditCard size={18} className="text-olive mt-0.5" /> Pay securely by card via Stripe Checkout.
                </p>
                <Button onClick={payWithStripe} disabled={paying} data-testid="pay-stripe"
                  className="mt-4 w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-12">
                  {paying ? "Redirecting to secure checkout..." : `Pay $${DEPOSIT} with Card`}
                </Button>
                <p className="mt-3 text-xs text-[#787069] text-center">
                  Secured by Stripe · You'll be redirected to complete payment
                </p>
              </TabsContent>
              <TabsContent value="paypal" className="pt-4">
                <p className="text-sm text-[#524B46] flex items-start gap-2 mb-4">
                  <Wallet size={18} className="text-olive mt-0.5" /> Pay your ${DEPOSIT} deposit securely with PayPal.
                </p>
                <PayPalDeposit
                  orderId={orderId}
                  amount={DEPOSIT}
                  name={form.name}
                  email={form.email}
                  flavor={form.base_flavor}
                  filling={form.filling}
                  servings={form.servings}
                  eventDate={date ? format(date, "PPP") : ""}
                  onSuccess={(cap) =>
                    setPayResult({
                      reference: cap.reference || cap.id,
                      amount: DEPOSIT,
                      method: "paypal",
                      instructions: {
                        note: "Payment received — thank you! We'll confirm your event date by email shortly.",
                      },
                    })
                  }
                />
              </TabsContent>
              <TabsContent value="zelle" className="pt-4">
                <p className="text-sm text-[#524B46] flex items-start gap-2">
                  <Send size={18} className="text-olive mt-0.5" /> Send via Zelle to our email; we'll confirm your date.
                </p>
                <Button onClick={() => pay("zelle")} disabled={paying} data-testid="pay-zelle"
                  className="mt-4 w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-12">
                  {paying ? "Processing..." : `Get Zelle Details`}
                </Button>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
